import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Button, Card, CardContent, Grid,
  Chip, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper, CssBaseline, Divider, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  ArrowBack, LocalShippingOutlined, CheckCircleOutline,
  HourglassEmptyOutlined, CancelOutlined, DeleteOutline, Inventory2Outlined,
  CallReceivedOutlined, DoNotDisturbAltOutlined, BlockOutlined,
  AssignmentReturnOutlined, PrintOutlined, WarningAmberOutlined,
  EditOutlined, LocalAtmOutlined, AddCircleOutline, ReceiptOutlined,
  HistoryOutlined, FingerprintOutlined,
} from '@mui/icons-material';
import { resumenCampos, esSerializado, nombreBusqueda } from './utils';
import AppTheme from '../../../shared-theme/AppTheme';
import SideMenu from '../../../components/SideMenu';
import Header from '../../../components/Header';
import { StyledCellHeader } from '../../../components/StyledCellHeader';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';
import OnGeneralMessageDialog from '../../../components/Dialogs/OnGeneralMessageDialog';
import RecibirDialog from './recibirdialog';
import CrearDevolucionDialog from './crearrevoluciondialog';
import { imprimirManifiestoTransferencia } from './pdfmanifest';
import { useNavigate, useParams } from 'react-router-dom';

const ESTADO_META = {
  pendiente:    { label: 'Pendiente',    color: 'warning', Icon: HourglassEmptyOutlined },
  en_transito:  { label: 'En tránsito',  color: 'info',    Icon: LocalShippingOutlined },
  recibida:     { label: 'Recibida',     color: 'success', Icon: CheckCircleOutline },
  rechazada:    { label: 'Rechazada',    color: 'error',   Icon: BlockOutlined },
  cancelada:    { label: 'Cancelada',    color: 'default', Icon: CancelOutlined },
};

const EVENTO_META = {
  creada:            { label: 'Creada',              Icon: AddCircleOutline,      color: 'info.main' },
  enviada:           { label: 'Enviada',             Icon: LocalShippingOutlined, color: 'info.main' },
  recibida:          { label: 'Recibida',            Icon: CheckCircleOutline,    color: 'success.main' },
  rechazada:         { label: 'Rechazada',           Icon: BlockOutlined,         color: 'error.main' },
  cancelada:         { label: 'Cancelada',           Icon: CancelOutlined,        color: 'text.secondary' },
  item_ajustado:     { label: 'Item ajustado',       Icon: WarningAmberOutlined,  color: 'warning.main' },
  devolucion_creada: { label: 'Devolución creada',   Icon: AssignmentReturnOutlined, color: 'warning.main' },
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Usuario-ID': localStorage.getItem('id'),
  'X-company-ID': localStorage.getItem('idcompany'),
});

const fmtFecha = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()} ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};

function DetailRow({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={600} color="text.primary">
        {value === null || value === undefined || value === '' ? '—' : value}
      </Typography>
    </Box>
  );
}

export default function DetalleTransferencia() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transferencia, setTransferencia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [recibirOpen, setRecibirOpen] = useState(false);
  const [devolucionOpen, setDevolucionOpen] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  const fetchDetalle = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/transferencias_mercancia/detalle/${id}`, { headers: authHeaders() });
      const data = await res.json();
      setTransferencia(data?.id ? data : null);
    } catch {
      setTransferencia(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetalle(); }, [fetchDetalle]);

  const callAction = async (endpoint, body = null) => {
    const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/transferencias_mercancia/${id}/${endpoint}`, {
      method: 'POST', headers: authHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Error');
    return result;
  };

  const handleEnviar = async () => {
    setAction('');
    try {
      await callAction('enviar', { enviado_por: localStorage.getItem('id') });
      setAlert({ open: true, message: 'Transferencia enviada. Stock descontado del origen.', severity: 'success' });
      fetchDetalle();
    } catch (err) { setAlert({ open: true, message: err.message, severity: 'error' }); }
  };

  const handleCancelar = async () => {
    setAction('');
    try {
      await callAction('cancelar', { cancelado_por: localStorage.getItem('id') });
      setAlert({ open: true, message: 'Transferencia cancelada', severity: 'success' });
      fetchDetalle();
    } catch (err) { setAlert({ open: true, message: err.message, severity: 'error' }); }
  };

  const handleEliminar = async () => {
    setAction('');
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/transferencias_mercancia/${id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error');
      }
      setAlert({ open: true, message: 'Transferencia eliminada', severity: 'success' });
      setTimeout(() => navigate('/logistica/transferencias'), 800);
    } catch (err) { setAlert({ open: true, message: err.message, severity: 'error' }); }
  };

  if (loading) {
    return (
      <AppTheme themeComponents={{}}>
        <CssBaseline enableColorScheme />
        <Box sx={{ display: 'flex' }}>
          <SideMenu />
          <Box sx={{ flexGrow: 1, p: 6, textAlign: 'center' }}><Typography color="text.secondary">Cargando…</Typography></Box>
        </Box>
      </AppTheme>
    );
  }

  if (!transferencia) {
    return (
      <AppTheme themeComponents={{}}>
        <CssBaseline enableColorScheme />
        <Box sx={{ display: 'flex' }}>
          <SideMenu />
          <Box sx={{ flexGrow: 1, p: 6, textAlign: 'center' }}>
            <Typography variant="h6">Transferencia no encontrada</Typography>
            <Button sx={{ mt: 2 }} onClick={() => navigate('/logistica/transferencias')}>Volver</Button>
          </Box>
        </Box>
      </AppTheme>
    );
  }

  const meta = ESTADO_META[transferencia.estado] || ESTADO_META.pendiente;
  const esPendiente = transferencia.estado === 'pendiente';
  const esTransito = transferencia.estado === 'en_transito';
  const esRecibida = transferencia.estado === 'recibida';
  const esFinal = ['recibida', 'rechazada', 'cancelada'].includes(transferencia.estado);

  return (
    <Box>
      <AppTheme themeComponents={{}}>
        <CssBaseline enableColorScheme />
        <Box sx={{ display: 'flex' }}>
          <SideMenu />
          <Box component="main" sx={{ flexGrow: 1, overflow: 'auto', bgcolor: 'background.default', minHeight: '100vh' }}>
            <Stack sx={{ alignItems: 'stretch', pb: 6, mt: { xs: 8, md: 0 } }}>
              <Header parent="Logística" child={`Transferencia ${transferencia.codigo}`} />

              <Box sx={{ px: { xs: 2, md: 4 }, pt: 3 }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Button variant="text" size="small" startIcon={<ArrowBack />} onClick={() => navigate('/logistica/transferencias')} sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'none' }}>Transferencias</Button>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                        <Typography variant="h5" fontWeight={800} letterSpacing="-0.5px">{transferencia.codigo}</Typography>
                        <Chip icon={<meta.Icon sx={{ fontSize: 16 }} />} label={meta.label} color={meta.color} sx={{ fontWeight: 600 }} />
                        {transferencia.transferencia_original_id && (
                          <Chip icon={<AssignmentReturnOutlined sx={{ fontSize: 14 }} />} label="Devolución" color="warning" size="small" sx={{ fontWeight: 600 }} />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" mt={0.25}>
                        {transferencia.almacen_origen_nombre} → {transferencia.almacen_destino_nombre}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button variant="outlined" size="small" startIcon={<PrintOutlined />} onClick={() => imprimirManifiestoTransferencia(transferencia, esRecibida || transferencia.estado === 'rechazada' ? 'recepcion' : 'envio')} sx={{ fontWeight: 600, textTransform: 'none' }}>
                      Imprimir {esFinal ? 'comprobante' : 'manifiesto'}
                    </Button>
                    {esPendiente && (
                      <>
                        <Button variant="outlined" color="error" size="small" startIcon={<DeleteOutline />} onClick={() => setAction('eliminar')} sx={{ fontWeight: 600, textTransform: 'none' }}>Eliminar</Button>
                        <Button variant="outlined" size="small" startIcon={<DoNotDisturbAltOutlined />} onClick={() => setAction('cancelar')} sx={{ fontWeight: 600, textTransform: 'none' }}>Cancelar</Button>
                        <Button variant="contained" color="primarybutton" size="small" startIcon={<LocalShippingOutlined />} onClick={() => setAction('enviar')} sx={{ fontWeight: 700, textTransform: 'none' }}>Enviar</Button>
                      </>
                    )}
                    {esTransito && (
                      <Button variant="contained" color="primarybutton" size="small" startIcon={<CallReceivedOutlined />} onClick={() => setRecibirOpen(true)} sx={{ fontWeight: 700, textTransform: 'none' }}>Recibir</Button>
                    )}
                    {esRecibida && !transferencia.transferencia_original_id && (
                      <Button variant="outlined" color="warning" size="small" startIcon={<AssignmentReturnOutlined />} onClick={() => setDevolucionOpen(true)} sx={{ fontWeight: 600, textTransform: 'none' }}>Crear devolución</Button>
                    )}
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item size={{ xs: 12, md: 4 }}>
                    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 2 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Envío</Typography>
                        <Stack spacing={2}>
                          <DetailRow label="Origen" value={transferencia.almacen_origen_nombre} />
                          <DetailRow label="Destino" value={transferencia.almacen_destino_nombre} />
                          <DetailRow label="Transportista" value={transferencia.transportista} />
                          <DetailRow label="Creada" value={`${fmtFecha(transferencia.created_at)} · ${transferencia.created_by_nombre || '—'}`} />
                          <DetailRow label="Observaciones" value={transferencia.observaciones_envio} />
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 2 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Seguimiento</Typography>
                        <Stack spacing={2}>
                          <DetailRow label="Enviada" value={transferencia.fecha_envio ? `${fmtFecha(transferencia.fecha_envio)} · ${transferencia.enviado_por_nombre || '—'}` : 'No enviada'} />
                          <DetailRow label="Recibida" value={transferencia.fecha_recepcion ? `${fmtFecha(transferencia.fecha_recepcion)} · ${transferencia.recibido_por_nombre || '—'}` : 'No recibida'} />
                          {transferencia.estado === 'rechazada' && (
                            <>
                              <DetailRow label="Rechazada" value={`${fmtFecha(transferencia.fecha_rechazo)} · ${transferencia.rechazada_por_nombre || '—'}`} />
                              <DetailRow label="Observaciones rechazo" value={transferencia.observaciones_rechazo} />
                            </>
                          )}
                          {transferencia.estado === 'cancelada' && (
                            <DetailRow label="Cancelada" value={`${fmtFecha(transferencia.fecha_cancelacion)} · ${transferencia.cancelado_por_nombre || '—'}`} />
                          )}
                          {transferencia.observaciones_recepcion && (
                            <DetailRow label="Observaciones recepción" value={transferencia.observaciones_recepcion} />
                          )}
                        </Stack>
                      </CardContent>
                    </Card>

                    {transferencia.devoluciones?.length > 0 && (
                      <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 2 }}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Devoluciones ligadas</Typography>
                          <Stack spacing={1}>
                            {transferencia.devoluciones.map(dev => {
                              const devMeta = ESTADO_META[dev.estado] || ESTADO_META.pendiente;
                              return (
                                <Stack key={dev.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ cursor: 'pointer', p: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate(`/logistica/transferencias/detalle/${dev.id}`)}>
                                  <Typography variant="body2" fontWeight={600}>{dev.codigo}</Typography>
                                  <Chip size="small" label={devMeta.label} color={devMeta.color} sx={{ fontWeight: 600 }} />
                                </Stack>
                              );
                            })}
                          </Stack>
                        </CardContent>
                      </Card>
                    )}

                    {transferencia.transferencia_original_id && (
                      <Button variant="outlined" size="small" fullWidth startIcon={<ArrowBack />} onClick={() => navigate(`/logistica/transferencias/detalle/${transferencia.transferencia_original_id}`)} sx={{ fontWeight: 600, textTransform: 'none', mb: 2 }}>
                        Ver transferencia original
                      </Button>
                    )}
                  </Grid>

                  <Grid item size={{ xs: 12, md: 8 }}>
                    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 2 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                          <Inventory2Outlined sx={{ fontSize: 18, mr: 0.75, verticalAlign: 'middle' }} />
                          Items ({transferencia.items?.length || 0})
                        </Typography>

                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <StyledCellHeader>Producto</StyledCellHeader>
                                <StyledCellHeader>Categoría</StyledCellHeader>
                                <StyledCellHeader sx={{ textAlign: 'center' }}>Enviado</StyledCellHeader>
                                <StyledCellHeader sx={{ textAlign: 'center' }}>Recibido</StyledCellHeader>
                                <StyledCellHeader>Motivo / Nota</StyledCellHeader>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(transferencia.items || []).map((it) => {
                                const diff = esFinal && it.cantidad_recibida !== it.cantidad_enviada;
                                const serial = esSerializado(it);
                                const primary = serial
                                  ? (nombreBusqueda({ ...it, nombre: it.producto_nombre }) || it.producto_nombre)
                                  : it.producto_nombre;
                                const campos = resumenCampos(it, 3, serial ? ['id'] : []);
                                return (
                                  <TableRow key={it.id} hover>
                                    <TableCell>
                                      <Stack direction="row" alignItems="center" spacing={0.5}>
                                        {serial && <FingerprintOutlined sx={{ fontSize: 14, color: 'primary.main' }} />}
                                        <Typography variant="body2" fontWeight={600}>{primary}</Typography>
                                      </Stack>
                                      {serial && <Typography variant="caption" color="text.secondary" display="block">{it.producto_nombre}</Typography>}
                                      {campos && <Typography variant="caption" color="text.secondary" display="block">{campos}</Typography>}
                                    </TableCell>
                                    <TableCell><Typography variant="caption" color="text.secondary">{it.categoria_nombre || '—'}</Typography></TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>{it.cantidad_enviada}</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      {esPendiente || esTransito ? (
                                        <Typography variant="body2" color="text.disabled">—</Typography>
                                      ) : it.rechazado ? (
                                        <Chip size="small" label="Rechazado" color="error" sx={{ fontWeight: 600 }} />
                                      ) : (
                                        <Typography variant="body2" fontWeight={600} color={diff ? 'warning.main' : 'text.primary'}>{it.cantidad_recibida}</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {it.motivo_rechazo_codigo && (
                                        <Typography variant="caption" fontWeight={600} color={it.rechazado ? 'error.main' : 'warning.main'} display="block">
                                          {it.motivo_rechazo_codigo.replace(/_/g, ' ')}
                                        </Typography>
                                      )}
                                      <Typography variant="caption" color="text.secondary">{it.nota_item || (it.motivo_rechazo_codigo ? '' : '—')}</Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                          <HistoryOutlined sx={{ fontSize: 18, mr: 0.75, verticalAlign: 'middle' }} />
                          Bitácora ({transferencia.eventos?.length || 0})
                        </Typography>
                        <List dense disablePadding>
                          {(transferencia.eventos || []).map(ev => {
                            const em = EVENTO_META[ev.tipo_evento] || { label: ev.tipo_evento, Icon: HistoryOutlined, color: 'text.secondary' };
                            let detalles = {};
                            try { detalles = ev.detalles ? (typeof ev.detalles === 'string' ? JSON.parse(ev.detalles) : ev.detalles) : {}; } catch { detalles = {}; }
                            return (
                              <ListItem key={ev.id} sx={{ py: 0.75, borderBottom: '1px dashed', borderColor: 'divider' }}>
                                <ListItemIcon sx={{ minWidth: 34 }}><em.Icon sx={{ fontSize: 18, color: em.color }} /></ListItemIcon>
                                <ListItemText
                                  primary={<Typography variant="body2" fontWeight={600}>{em.label}</Typography>}
                                  secondary={
                                    <Typography variant="caption" color="text.secondary">
                                      {fmtFecha(ev.fecha)} · {ev.usuario_nombre || '—'}
                                      {detalles.producto && <> · {detalles.producto}</>}
                                      {detalles.motivo && <> · motivo: {String(detalles.motivo).replace(/_/g, ' ')}</>}
                                      {detalles.transportista && <> · transportista: {detalles.transportista}</>}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            );
                          })}
                          {(!transferencia.eventos || transferencia.eventos.length === 0) && (
                            <Typography variant="caption" color="text.secondary">Sin eventos registrados.</Typography>
                          )}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </Box>
        </Box>

        <RecibirDialog
          open={recibirOpen}
          onClose={() => setRecibirOpen(false)}
          transferencia={transferencia}
          onDone={(result) => {
            setAlert({ open: true, message: result?.estado === 'rechazada' ? 'Transferencia rechazada. Stock devuelto al origen.' : 'Transferencia recibida.', severity: 'success' });
            fetchDetalle();
          }}
        />

        <CrearDevolucionDialog
          open={devolucionOpen}
          onClose={() => setDevolucionOpen(false)}
          transferencia={transferencia}
          onDone={(result) => {
            setAlert({ open: true, message: `Devolución creada: ${result?.codigo || ''}`, severity: 'success' });
            fetchDetalle();
            if (result?.id) setTimeout(() => navigate(`/logistica/transferencias/detalle/${result.id}`), 500);
          }}
        />

        <OnGeneralMessageDialog
          open={action === 'enviar'} onClose={() => setAction('')}
          title="Confirmar envío"
          message="Al enviar la transferencia se descontará el stock del almacén origen. Esta acción no se puede deshacer."
          buttonText="Sí, enviar" buttonColor="primarybutton" onRegister={handleEnviar}
        />
        <OnGeneralMessageDialog
          open={action === 'cancelar'} onClose={() => setAction('')}
          title="Cancelar transferencia"
          message="¿Seguro que querés cancelar esta transferencia?"
          buttonText="Sí, cancelar" buttonColor="error" onRegister={handleCancelar}
        />
        <OnGeneralMessageDialog
          open={action === 'eliminar'} onClose={() => setAction('')}
          title="Eliminar transferencia"
          message="¿Seguro que querés eliminar esta transferencia?"
          buttonText="Sí, eliminar" buttonColor="error" onRegister={handleEliminar}
        />

        <AlertMessageDialog open={alert.open} handleClose={() => setAlert({ ...alert, open: false })} title="" message={alert.message} severity={alert.severity} color={alert.severity} />
      </AppTheme>
    </Box>
  );
}
